import {IconButton} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {OpenLine} from '@dropbox/dig-icons/assets';
import {useQuery} from '@tanstack/react-query';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {Title} from 'components/DSYS/Title';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useLocation} from 'react-router';

import {buildDropboxPaperUrl, fetchPaperPreview} from './api';
import styles from './PaperPreview.module.css';

export const PaperPreview = () => {
  const location = useLocation();
  const sourceUrl = buildDropboxPaperUrl(location.pathname, location.search);
  const {data, error, isLoading} = useQuery({
    queryKey: ['paper-preview', 'html-v1', sourceUrl],
    queryFn: () => fetchPaperPreview(sourceUrl),
    retry: false,
  });

  useDocumentTitle(data?.title ?? t('paper_preview_title'));

  if (isLoading) {
    return (
      <Box className={styles.loading} aria-label={t('paper_preview_loading')}>
        <Spinner size="medium" />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box padding="32" className={styles.error}>
        <SystemBanner tone="alert">
          <Text>
            <strong>{t('paper_preview_unavailable_title')}</strong>{' '}
            {t('paper_preview_unavailable_body')}
          </Text>
        </SystemBanner>
      </Box>
    );
  }

  return (
    <Box padding="32" paddingBottom="100">
      <main className={styles.document}>
        <header className={styles.header}>
          <Title className={styles.title} size={40}>
            {data.title}
          </Title>
          <Tooltip title={t('paper_preview_open_original')}>
            <IconButton
              className={styles.openOriginal}
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="borderless"
              aria-label={t('paper_preview_open_original')}
            >
              <UIIcon src={OpenLine} />
            </IconButton>
          </Tooltip>
        </header>
        <RichTextArea
          value={data.content}
          editable={false}
          autoFocus={false}
          profile="paper"
          source="paper-preview"
          contentClassName={styles.content}
          openExternalLinksInNewTab
          enhancedTableCells
          disableBorder
          table
          media
        />
      </main>
    </Box>
  );
};
